import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cbCn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const cb = {
  panel: (dark?: boolean, pad?: "sm" | "md") =>
    cbCn(
      "rounded-xl border shadow-sm",
      pad === "sm" ? "p-4" : "p-6",
      dark ? "border-slate-700 bg-slate-900/95" : "border-slate-200 bg-white"
    ),
  heading: (dark?: boolean, large?: boolean) =>
    cbCn("font-semibold", large ? "text-lg" : "text-base", dark ? "text-slate-100" : "text-slate-900"),
  headingLg: (dark?: boolean) => cbCn("font-bold text-2xl", dark ? "text-slate-100" : "text-slate-900"),
  body: (dark?: boolean) => (dark ? "text-slate-300" : "text-slate-600"),
  bodySm: (dark?: boolean) => (dark ? "text-slate-400" : "text-slate-500"),
  muted: (dark?: boolean) =>
    cbCn("rounded-lg border px-3 py-2 text-xs", dark ? "border-slate-600 bg-slate-800/80 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"),
  inset: (dark?: boolean) =>
    cbCn("rounded-lg border", dark ? "border-slate-700 bg-slate-800/60" : "border-slate-200 bg-slate-50/50"),
  templateRow: (dark?: boolean) =>
    cbCn("rounded-lg border", dark ? "border-slate-700 bg-slate-800/70" : "border-slate-200 bg-slate-50/50"),
  input: (dark?: boolean, locked?: boolean) =>
    locked
      ? dark
        ? "cursor-not-allowed border-slate-600 bg-slate-800 text-slate-400 placeholder:text-slate-500"
        : "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-700 placeholder:text-slate-400"
      : dark
        ? "border-slate-600 bg-slate-800 text-slate-100 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-indigo-500"
        : "border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:ring-indigo-500",
  label: (dark?: boolean) => (dark ? "text-slate-300" : "text-slate-700"),
  radio: (dark?: boolean) =>
    cbCn(
      "h-4 w-4 shrink-0 border text-indigo-600 focus:ring-indigo-500 focus:ring-offset-0",
      dark ? "border-slate-500 bg-slate-800 focus:ring-offset-slate-900" : "border-slate-300 bg-white focus:ring-offset-white"
    ),
  alertError: (dark?: boolean) =>
    dark ? "border-red-500/40 bg-red-950/40 text-red-200" : "border-red-200 bg-red-50 text-red-700",
  alertSuccess: (dark?: boolean) =>
    dark ? "border-emerald-500/40 bg-emerald-950/40 text-emerald-200" : "border-emerald-200 bg-emerald-50 text-emerald-700",
  listRow: (dark?: boolean, suspended?: boolean) =>
    suspended
      ? dark
        ? "border-amber-600/50 bg-amber-950/30"
        : "border-amber-200 bg-amber-50/50"
      : dark
        ? "border-emerald-600/50 bg-emerald-950/30"
        : "border-emerald-200 bg-emerald-50/50",
  tag: (
    dark?: boolean,
    tone: "indigo" | "slate" | "amber" | "emerald" | "emeraldSoft" = "slate"
  ) => {
    const tones = {
      indigo: dark ? "bg-indigo-950/70 text-indigo-200" : "bg-indigo-100 text-indigo-800",
      slate: dark ? "bg-slate-700 text-slate-200" : "bg-slate-200 text-slate-700",
      amber: dark ? "bg-amber-950/60 text-amber-200" : "bg-amber-200 text-amber-800",
      emerald: dark ? "bg-emerald-950/60 text-emerald-200" : "bg-emerald-200 text-emerald-800",
      emeraldSoft: dark ? "bg-emerald-950/50 text-emerald-300" : "bg-emerald-100 text-emerald-800",
    };
    return cbCn("inline-flex h-6 shrink-0 items-center rounded px-2 text-[11px] font-medium leading-none", tones[tone]);
  },
  iconBtn: (dark?: boolean, isMobile?: boolean) =>
    cbCn(
      "rounded disabled:cursor-default disabled:opacity-40",
      dark ? "text-slate-300 hover:bg-slate-700 hover:text-slate-100" : "text-slate-600 hover:bg-slate-200 hover:text-slate-900",
      isMobile ? "min-h-[44px] min-w-[44px] p-2.5" : "p-1.5"
    ),
  iconBtnHover: (dark?: boolean, tone: "emerald" | "violet" | "indigo" | "sky" | "amber" | "red" = "emerald") => {
    const tones = {
      emerald: dark ? "hover:bg-emerald-950/60 hover:text-emerald-300" : "hover:bg-emerald-100 hover:text-emerald-800",
      violet: dark ? "hover:bg-violet-950/60 hover:text-violet-300" : "hover:bg-violet-100 hover:text-violet-700",
      indigo: dark ? "hover:bg-indigo-950/60 hover:text-indigo-300" : "hover:bg-indigo-100 hover:text-indigo-700",
      sky: dark ? "hover:bg-sky-950/60 hover:text-sky-300" : "hover:bg-sky-100 hover:text-sky-700",
      amber: dark ? "hover:bg-amber-950/60 hover:text-amber-300" : "hover:bg-amber-100 hover:text-amber-700",
      red: dark ? "hover:bg-red-950/60 hover:text-red-300" : "hover:bg-red-100 hover:text-red-600",
    };
    return tones[tone];
  },
  modal: (dark?: boolean) =>
    dark
      ? "rounded-xl border border-slate-600 bg-slate-900 shadow-xl"
      : "rounded-xl border border-slate-200 bg-white shadow-xl",
  stepActive: (dark?: boolean) =>
    dark
      ? "border border-indigo-400/80 bg-indigo-950/90 text-indigo-50 shadow-sm shadow-indigo-950/60"
      : "border border-indigo-200 bg-indigo-100 text-indigo-900",
  stepInactive: (dark?: boolean) =>
    dark
      ? "border border-slate-600 bg-slate-800/90 text-slate-300"
      : "border border-transparent bg-slate-100 text-slate-600",
  btnPrimary: (disabled?: boolean) =>
    disabled
      ? "cursor-not-allowed bg-slate-600 text-slate-300"
      : "bg-indigo-600 text-white hover:bg-indigo-700",
  btnSecondary: (dark?: boolean) =>
    dark
      ? "border border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700"
      : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
  btnGhost: (dark?: boolean) => (dark ? "text-slate-300 hover:bg-slate-800" : "text-slate-600 hover:bg-slate-100"),
  badge: (dark?: boolean, tone: "indigo" | "sky" = "indigo") =>
    tone === "sky"
      ? dark
        ? "rounded-full bg-sky-950/50 px-3 py-1 font-medium text-sky-200"
        : "rounded-full bg-sky-50 px-3 py-1 font-medium text-sky-800"
      : dark
        ? "rounded-full bg-indigo-950/50 px-3 py-1 font-medium text-indigo-200"
        : "rounded-full bg-indigo-50 px-3 py-1 font-medium text-indigo-800",
  dropZone: (dark: boolean | undefined, accent: "emerald" | "amber", isOver: boolean) => {
    if (dark) {
      if (accent === "emerald") {
        return isOver
          ? "border-emerald-400 bg-emerald-950/50"
          : "border-emerald-600/45 bg-emerald-950/25";
      }
      return isOver ? "border-amber-400 bg-amber-950/50" : "border-amber-600/45 bg-amber-950/25";
    }
    if (accent === "emerald") {
      return isOver ? "border-emerald-500 bg-emerald-50" : "border-emerald-200 bg-emerald-50/40";
    }
    return isOver ? "border-amber-500 bg-amber-50" : "border-amber-200 bg-amber-50/40";
  },
  dropZoneTitle: (dark: boolean | undefined, accent: "emerald" | "amber") =>
    accent === "emerald" ? (dark ? "text-emerald-300" : "text-emerald-900") : dark ? "text-amber-300" : "text-amber-900",
  dropEmpty: (dark?: boolean) =>
    dark
      ? "border-dashed border-slate-600 bg-slate-800/40 text-slate-400"
      : "border-dashed border-slate-300/80 bg-white/50 text-slate-500",
  criterionCard: (dark?: boolean, flashing?: boolean) =>
    flashing
      ? dark
        ? "border-indigo-500 bg-indigo-950/60 ring-2 ring-indigo-400/75 ring-offset-2 ring-offset-slate-900"
        : "border-indigo-400 bg-indigo-50/95 ring-2 ring-indigo-500/75 ring-offset-2 ring-offset-white"
      : dark
        ? "border-slate-600 bg-slate-800/90"
        : "border-slate-200 bg-white",
  policyToggle: (dark?: boolean, active?: boolean) =>
    active
      ? dark
        ? "bg-slate-700 text-slate-100 shadow-sm"
        : "bg-white text-slate-900 shadow-sm"
      : dark
        ? "text-slate-400 hover:text-slate-200"
        : "text-slate-600 hover:text-slate-900",
  policyGroup: (dark?: boolean) =>
    dark
      ? "flex shrink-0 rounded-xl border border-slate-600 bg-slate-800/80 p-1"
      : "flex shrink-0 rounded-xl border border-slate-200 bg-slate-50/80 p-1",
  hint: (dark?: boolean) => (dark ? "text-amber-200" : "text-amber-800"),
  divider: (dark?: boolean) => (dark ? "border-slate-700" : "border-slate-200"),
  rowHover: (dark?: boolean) => (dark ? "hover:bg-slate-700/80" : "hover:bg-slate-100"),
  /** Choice cards for build method (template vs custom). */
  choiceCard: (dark?: boolean, selected?: boolean) =>
    cbCn(
      "group relative flex w-full flex-col gap-2 rounded-2xl border-2 p-4 text-left transition-all duration-200 sm:p-5",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
      dark ? "focus-visible:ring-offset-slate-900" : "focus-visible:ring-offset-white",
      selected
        ? dark
          ? "border-indigo-400 bg-indigo-950/50 shadow-lg shadow-indigo-950/40"
          : "border-indigo-500 bg-indigo-50/90 shadow-lg shadow-indigo-200/60"
        : dark
          ? "border-slate-600 bg-slate-800/50 hover:border-indigo-500/50 hover:bg-slate-800 hover:shadow-md"
          : "border-slate-200 bg-white hover:border-indigo-300 hover:bg-slate-50/80 hover:shadow-md"
    ),
  choiceCardIcon: (dark?: boolean, selected?: boolean) =>
    cbCn(
      "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-colors",
      selected
        ? dark
          ? "bg-indigo-500/30 text-indigo-200"
          : "bg-indigo-100 text-indigo-700"
        : dark
          ? "bg-slate-700/80 text-slate-300 group-hover:bg-slate-700 group-hover:text-slate-100"
          : "bg-slate-100 text-slate-600 group-hover:bg-indigo-50 group-hover:text-indigo-700"
    ),
  accordionShell: (dark?: boolean) =>
    cbCn(
      "overflow-hidden rounded-xl border shadow-sm transition-shadow duration-200",
      dark ? "border-slate-700 bg-slate-900/95" : "border-slate-200 bg-white"
    ),
  accordionHeader: (dark?: boolean, open?: boolean, disabled?: boolean) =>
    cbCn(
      "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-150 sm:px-5 sm:py-3.5",
      disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
      !disabled && (dark ? "hover:bg-slate-800/80" : "hover:bg-slate-50"),
      open && (dark ? "bg-slate-800/40" : "bg-slate-50/60")
    ),
  accordionBody: (dark?: boolean, pad?: "sm" | "md") =>
    cbCn(
      "border-t transition-[opacity,transform] duration-200 ease-out",
      pad === "sm" ? "p-4" : "p-5 sm:p-6",
      dark ? "border-slate-700" : "border-slate-200"
    ),
  stepPill: (dark?: boolean, state: "active" | "done" | "idle" = "idle", clickable?: boolean) =>
    cbCn(
      "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-all duration-200",
      state === "active" &&
        (dark
          ? "border-indigo-400/80 bg-indigo-950/90 text-indigo-50 shadow-sm shadow-indigo-950/60"
          : "border-indigo-200 bg-indigo-100 text-indigo-900"),
      state === "done" &&
        (dark
          ? "border-emerald-500/40 bg-emerald-950/40 text-emerald-200"
          : "border-emerald-200 bg-emerald-50 text-emerald-800"),
      state === "idle" &&
        (dark
          ? "border-slate-600 bg-slate-800/90 text-slate-400"
          : "border-transparent bg-slate-100 text-slate-500"),
      clickable && (dark ? "cursor-pointer hover:brightness-110" : "cursor-pointer hover:brightness-95"),
      !clickable && "cursor-default"
    ),
  dangerBtn: (dark?: boolean) =>
    dark
      ? "rounded border border-rose-500/40 bg-rose-950/40 px-2 py-1 text-xs font-medium text-rose-200 hover:bg-rose-950/70"
      : "rounded border border-rose-200 bg-rose-50 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100",
  guideTrack: (dark?: boolean) =>
    cbCn(
      "rounded-2xl border px-3 py-3 sm:px-4",
      dark
        ? "border-slate-700 bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950/40"
        : "border-slate-200 bg-gradient-to-br from-white via-slate-50 to-indigo-50/60"
    ),
  guideDot: (dark?: boolean, state: "active" | "done" | "idle" = "idle") =>
    cbCn(
      "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all",
      state === "active" &&
        (dark
          ? "bg-indigo-500 text-white shadow-md shadow-indigo-950/50"
          : "bg-indigo-600 text-white shadow-md shadow-indigo-200"),
      state === "done" &&
        (dark ? "bg-emerald-950/80 text-emerald-300" : "bg-emerald-100 text-emerald-700"),
      state === "idle" && (dark ? "bg-slate-800 text-slate-400" : "bg-slate-200 text-slate-600")
    ),
  stageCard: (dark?: boolean) =>
    cbCn(
      "rounded-2xl border p-5 shadow-sm sm:p-6",
      dark ? "border-slate-700 bg-slate-900/95" : "border-slate-200 bg-white"
    ),
  accentBtn: (dark?: boolean, tone: "emerald" | "amber" | "indigo" = "indigo") => {
    if (tone === "emerald") {
      return dark
        ? "rounded border border-emerald-500/40 bg-emerald-950/40 px-2 py-1 text-xs font-medium text-emerald-200 hover:bg-emerald-950/70 disabled:cursor-not-allowed disabled:opacity-50"
        : "rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-900 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50";
    }
    if (tone === "amber") {
      return dark
        ? "rounded border border-amber-500/40 bg-amber-950/40 px-2 py-1 text-xs font-medium text-amber-200 hover:bg-amber-950/70 disabled:cursor-not-allowed disabled:opacity-50"
        : "rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50";
    }
    return dark
      ? "rounded border border-indigo-500/40 bg-indigo-950/40 px-2 py-1 text-xs font-medium text-indigo-200 hover:bg-indigo-950/70 disabled:cursor-not-allowed disabled:opacity-50"
      : "rounded border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-900 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50";
  },
};
